import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import API from "../utils/api";
import { Clock, CheckCircle, ClipboardList, User } from "lucide-react";
import { toast } from "react-toastify";

export default function ActivityTimeline({ token, leadId }) {
  const [events, setEvents] = useState([]);
  const socketRef = useRef(null);

  // Fetch initial history
  useEffect(() => {
    if (!leadId || !token) return;
    const fetchActivity = async () => {
      try {
        const res = await API.get(`leads/${leadId}/activity/`);
        setEvents(res.data);
      } catch (err) {
        // Handle 404 gracefully - endpoint doesn't exist yet
        if (err.response?.status === 404) {
          setEvents([]);
          return;
        }
        console.error("Error fetching activity:", err);
        setEvents([]);
      }
    };
    fetchActivity();
  }, [leadId, token]);

  // Live WebSocket updates (optional - gracefully handles if not available)
  useEffect(() => {
  if (!leadId) return;

  // 🔒 WebSocket is OPTIONAL — hard gated
  if (process.env.REACT_APP_ENABLE_WS !== "true") {
    return;
  }

  let socket;

  try {
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = process.env.REACT_APP_WS_URL
      ? process.env.REACT_APP_WS_URL.replace(/^https?:\/\//, "")
      : "127.0.0.1:8000";

    const wsUrl = `${wsProtocol}//${wsHost}/ws/leads/${leadId}/`;

    socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = () => {
      // intentionally silent
    };

    socket.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setEvents((prev) => [
          {
            type: data.event_type,
            description: data.message,
            user: data.user,
            timestamp: new Date().toISOString(),
          },
          ...prev,
        ]);
      } catch {
        // swallow parse errors
      }
    };

    socket.onerror = () => {
      // silent — WS is optional
    };

    socket.onclose = () => {
      // silent
    };
  } catch {
    // silent
  }

  return () => {
    try {
      socket?.close();
    } catch {
      // ignore
    }
  };
}, [leadId]);


  const getIcon = (type) => {
    const iconProps = { size: 10, style: { flexShrink: 0 } };
    switch (type) {
      case "Lead Created":
        return <User {...iconProps} style={{ ...iconProps.style, color: "#2563eb" }} />;
      case "Follow-up":
      case "Follow-up Created":
        return <Clock {...iconProps} style={{ ...iconProps.style, color: "#16a34a" }} />;
      case "Task":
        return <ClipboardList {...iconProps} style={{ ...iconProps.style, color: "#ca8a04" }} />;
      default:
        return <CheckCircle {...iconProps} style={{ ...iconProps.style, color: "#6b7280" }} />;
    }
  };

  return (
    <Box>
      {events.length > 0 ? (
        <Box 
          sx={{ 
            maxHeight: "140px", 
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.3)",
              },
            },
          }}
        >
          <Box sx={{ position: "relative", borderLeft: "1px solid", borderColor: "divider", ml: 1.5, pl: 2 }}>
            {events.map((event, i) => (
              <Box key={i} sx={{ mb: 2, position: "relative" }}>
                <Box sx={{ position: "absolute", left: -13, top: 2, bgcolor: "background.paper" }}>
                  {getIcon(event.type)}
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", fontWeight: 600, fontSize: "0.7rem" }}>
                    {event.type}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.65rem", mt: 0.25 }}>
                    {event.description}
                  </Typography>
                  {event.user && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.6rem", mt: 0.25 }}>
                      by <Box component="span" sx={{ fontWeight: 600 }}>{event.user}</Box>
                    </Typography>
                  )}
                  {event.timestamp && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", fontSize: "0.6rem", mt: 0.25 }}>
                      {new Date(event.timestamp).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          No activity recorded yet.
        </Typography>
      )}
    </Box>
  );
}
